// src/lib/econSnapshot.ts
//
// Builds the advisor-facing "Economic Snapshot" markdown export from the same
// /econ/overview payload the Economics page already renders.
//
// DESIGN NOTE (important — read before editing):
//
// The Economics dashboard is a CIO-only instrument panel. Its content cannot be
// dumped verbatim into an advisor-facing document, for two separate reasons:
//
//   1. Operational plumbing (feed health, ML run ids, model-inclusion flags,
//      FRED series ids, the heuristic-fallback banner, the internal review
//      playbook) is meaningless or actively misleading outside the firm. This
//      module simply never emits it.
//
//   2. The engine's own prose is written for the CIO. econ_overview's
//      _macro_narrative() emits phrases like "the expanded dashboard", "keep
//      economic outputs as CIO decision support", and "the separation between
//      Strategy Engine, Trading Desk, and CIO approval". Those name internal
//      systems and internal governance. So this module does NOT pass the engine
//      narrative through. It carries the engine's *facts* (indicator values,
//      dates, scores, dimension statuses) and pairs them with advisor-
//      appropriate framing copy selected by the same regime label the engine
//      computed.
//
// Consequence: REGIME_COPY / BAND_COPY / POSITIONING_COPY below are editorial
// content, not derived data. They require CIO and compliance sign-off, and they
// are the only place in this file where wording is authored rather than read.
//
// The file carries no DRAFT / review watermark (CIO decision, 2026-09-08). This
// is an intermediate artifact handed to a designer, and a process marker in it
// would either get typeset into the finished factsheet or have to be remembered
// and stripped. The compliance gate sits on the designed piece before it is
// distributed, not as a string in a generated file. The substantive legal
// content -- the DISCLOSURES block -- stays in and travels with the document.
//
// Model probabilities are rendered as WORDS, never percentages (CIO decision,
// 2026-09-08). Observed economic data — Core CPI, unemployment, spreads — keeps
// its actual numbers; those are measurements, not forecasts. The distinction is
// the point: we publish what the economy did, not what our model predicts.

export type EconSnapshotIndicator = {
  series_id: string;
  label: string;
  value: number;
  score: number;
  score_label: string;
  class: string;
  type: string;
  theme?: string;
  unit?: string;
  interpretation?: string;
  context_role?: string | null;
  date: string;
};

export type EconSnapshotDimension = {
  label: string;
  status: string;
};

export type EconSnapshotRegimeHistory = {
  dates: string[];
  expansionary: number[];
  cautionary: number[];
  recessionary: number[];
  weakening_prob: number[];
};

export type EconSnapshotBands = {
  sentinel: number;
  monitor: number;
  watch: number;
  caution: number;
  severe: number;
};

export type EconSnapshotHistory = {
  label: string;
  dates: string[];
  values: Array<number | null>;
  unit?: string | null;
  cadence?: string | null;
};

export type EconSnapshotInput = {
  /** Latest data date across the indicator set (not the generation time). */
  asOf: string | null;
  /** When the operator pressed the button. */
  generatedAt: string;
  /** "Expansionary" | "Cautionary" | "Recessionary" — engine's regime label. */
  regimeLabel: string;
  /** True when the trained model was unavailable and a heuristic stood in. */
  regimeIsFallback: boolean;
  /** Operating band from bandForProbability(): Benign..Severe. */
  riskBand: string;
  /** Direction from trendDescriptor(): Stable / Rising modestly / ... */
  riskTrend: string;
  dimensions: EconSnapshotDimension[];
  supports: EconSnapshotIndicator[];
  risks: EconSnapshotIndicator[];
  indicators: EconSnapshotIndicator[];
  /** regime.history from the payload: 36 trailing monthly points. */
  regimeHistory: EconSnapshotRegimeHistory;
  /**
   * The page's own 3-month rolling weakening line, ALREADY IN PERCENT and index-
   * aligned to regimeHistory.dates. Passed in rather than recomputed so the
   * document and the chart on the board cannot diverge.
   */
  weakeningRolling3mPct: Array<number | null>;
  /** Operating band lower bounds, as fractions (0-1). */
  bands: EconSnapshotBands;
  /** overview.histories, keyed by series id. */
  histories: Record<string, EconSnapshotHistory>;
  /** Page's own value formatter, passed in so the doc and the board never drift. */
  formatValue: (item: Pick<EconSnapshotIndicator, "value" | "unit">) => string;
  /** Page's own date formatter, same reason. */
  formatDate: (value: string | null | undefined) => string;
};

// ---------------------------------------------------------------------------
// Editorial copy — needs CIO + compliance sign-off. See DESIGN NOTE above.
// ---------------------------------------------------------------------------

type RegimeCopy = { stance: string; reading: string };

const REGIME_COPY: Record<string, RegimeCopy> = {
  Expansionary: {
    stance: "Expansionary",
    reading:
      "Our macro framework currently reads the economic backdrop as expansionary. Growth, labor, inflation, and financial conditions are assessed separately, and on balance they are not signalling broad economic contraction.",
  },
  Cautionary: {
    stance: "Cautionary",
    reading:
      "Our macro framework currently reads the economic backdrop as cautionary. Pressure has built in parts of the economy without spreading broadly enough to indicate contraction. This is a mixed picture rather than a deteriorating one.",
  },
  Recessionary: {
    stance: "Defensive",
    reading:
      "Our macro framework currently reads the economic backdrop as defensive. Enough separate parts of the economy are showing strain that we treat recession risk as a live consideration rather than a tail case.",
  },
};

const DEFAULT_REGIME_COPY: RegimeCopy = {
  stance: "Under review",
  reading:
    "Our macro framework did not resolve to a standard reading for this period. Treat this snapshot as incomplete and refer to the accompanying commentary.",
};

// Keyed to the operating bands the dashboard already uses, so the document and
// the board describe the same state. No probabilities are published.
const BAND_COPY: Record<string, string> = {
  Benign: "Early-warning indicators are quiet.",
  Sentinel: "Early-warning indicators are slightly elevated but remain within normal ranges.",
  Monitor: "Early-warning indicators have moved enough to warrant closer monitoring.",
  Watch: "Early-warning indicators are elevated and under formal review.",
  Caution: "Early-warning indicators are meaningfully elevated and we are actively reviewing positioning.",
  Severe: "Early-warning indicators are at levels that trigger our highest-priority macro review.",
};

const TREND_COPY: Record<string, string> = {
  "Rising sharply": "and have risen sharply over the last three months",
  "Rising modestly": "and have risen modestly over the last three months",
  "Falling sharply": "and have fallen sharply over the last three months",
  "Falling modestly": "and have eased modestly over the last three months",
  Stable: "and have been broadly stable over the last three months",
};

const POSITIONING_COPY: Record<string, string> = {
  Expansionary:
    "A supportive backdrop does not change how the strategy is built. Our exposure is expressed through long-dated index call options, which carry a defined and pre-funded maximum loss regardless of the macro reading. What the macro work informs is pacing — how quickly capital is deployed, and how we think about the balance between the options sleeve and the fixed income sleeve.",
  Cautionary:
    "A mixed backdrop does not change how the strategy is built. Our exposure is expressed through long-dated index call options, which carry a defined and pre-funded maximum loss regardless of the macro reading. What the macro work informs is pacing — how quickly capital is deployed, and how we think about the balance between the options sleeve and the fixed income sleeve.",
  Recessionary:
    "A defensive backdrop does not change how the strategy is built. Our exposure is expressed through long-dated index call options, which carry a defined and pre-funded maximum loss regardless of the macro reading. What the macro work informs is pacing — how quickly capital is deployed, and the emphasis we place on liquidity and quality within the fixed income sleeve.",
};

const DEFAULT_POSITIONING =
  "Our exposure is expressed through long-dated index call options, which carry a defined and pre-funded maximum loss regardless of the macro reading. The macro work informs pacing and sleeve balance, not the structure of the position.";

// Every scoring run re-scores the ENTIRE feature history with the current model
// and overwrites regime_probs.csv (HelionBackend/ml_macro_model/score_model.py,
// _predict_history -> one predict_proba over all rows). The model is retrained
// monthly. So the historical portion of these lines is the current model applied
// to past data, restated on each retrain -- NOT what the framework said at the
// time. Publishing it to advisers without saying so would imply a real-time
// track record that does not exist.
const HISTORICAL_RESTATEMENT_NOTE =
  "**How to read the history.** The whole line, not just the latest point, is produced by applying the framework's current model to historical data. It is recalculated when the model is retrained, so earlier points can move between one snapshot and the next. It shows how today's framework reads the past. It is not a record of assessments published at the time, and it is not a track record of calls made in real time.";

const DISCLOSURES = [
  "This material is provided for informational and discussion purposes only. It is not an offer to sell or a solicitation of an offer to buy any security or interest in any fund. Interests are offered solely through the applicable private placement memorandum, operating agreement, subscription documents, and other governing documents, which contain important information about risks, fees, and conflicts of interest, and which control in the event of any inconsistency with this material.",
  "The economic assessments described here are the output of a proprietary, rules-based framework applied to publicly available economic data. They describe how we characterize current conditions. They are not forecasts, predictions, or guarantees of future economic or market outcomes, and they should not be relied upon as such. Economic conditions can change rapidly and without warning.",
  "Any chart or series in this material showing historical framework readings is generated by applying the current model to historical data. Such output is hypothetical, is recalculated when the model is retrained, and does not represent assessments that were published, relied upon, or acted upon at the time. It is not a track record. Hypothetical and backtested results have inherent limitations, including the benefit of hindsight, and are not indicative of future results.",
  "Economic data is published with a lag, is frequently revised after initial release, and may be restated materially. Figures shown reflect the most recent values available as of the date noted and may differ from later revisions.",
  "Options strategies are complex and involve substantial risk, including the risk of loss of the entire amount invested. Nothing in this material should be construed as a representation that any strategy will achieve its objectives, limit losses, reduce risk, or produce any particular return. Past performance, hypothetical performance, and backtested results are not indicative of future results.",
  "Nothing in this material constitutes tax, legal, accounting, or investment advice. Recipients should consult their own advisers regarding their particular circumstances.",
  "Statements regarding portfolio positioning describe the framework's general approach and are subject to change at the manager's discretion without notice.",
];

const SOURCE_NOTE =
  "Underlying economic data is sourced from the Federal Reserve Bank of St. Louis (FRED) and the statistical agencies that publish to it, including the Bureau of Labor Statistics, the Bureau of Economic Analysis, the Census Bureau, and the Federal Reserve. Indicator readings are classified by a rules-based framework across growth, labor, inflation and Fed pressure, financial conditions, consumer and housing, and GDP context, so that no single series determines the overall assessment.";

// ---------------------------------------------------------------------------
// Sanitizing
// ---------------------------------------------------------------------------

// Indicator interpretations in the catalog are written for internal use, and a
// handful reference model plumbing ("kept out of core AML training because...").
// Drop any sentence that names internal machinery; keep the rest.
const INTERNAL_JARGON =
  /\b(AML|train(?:ing|ed)?|feature|breadth|backtest|model|scored?|scores|dashboard|CIO|Strategy Engine|Trading Desk)\b/i;

export function sanitizeForAdvisor(text?: string | null): string {
  if (!text) return "";
  // Split on sentence boundaries without lookbehind: Safari < 16.4 throws a
  // SyntaxError when *parsing* a lookbehind literal, which would take the whole
  // Economics page down rather than just this export.
  const sentences = String(text).match(/[^.!?]+[.!?]*/g) || [];
  return sentences
    .map((sentence) => sentence.trim())
    .filter(Boolean)
    .filter((sentence) => !INTERNAL_JARGON.test(sentence))
    .join(" ")
    .trim();
}

function escapePipes(value: string): string {
  return value.replace(/\|/g, "\\|");
}

function themeFor(indicator: EconSnapshotIndicator): string {
  return indicator.theme || indicator.class || "Other";
}

// Series offered to the designer as chart data. Curated on purpose: the payload
// carries 23 chartable series, which is a menu nobody wants. These five plus the
// two framework series tell the macro story without burying the layout.
// Public, published government data -- no forecast content.
const CHART_SERIES: Array<{ id: string; note: string }> = [
  { id: "CPILFESL", note: "Core CPI, year over year. Excludes food and energy." },
  { id: "UNRATE", note: "Civilian unemployment rate." },
  {
    id: "T10Y2Y",
    note: "10-year minus 2-year Treasury yield, in percentage points. Negative values mean an inverted curve.",
  },
  {
    id: "BAA10YM",
    note: "Baa corporate bond yield minus the 10-year Treasury, in percentage points. A credit-stress gauge.",
  },
  {
    id: "CFNAIMA3",
    note: "Chicago Fed National Activity Index, three-month average. Zero is trend growth; below zero is below trend.",
  },
];

const CHART_MAX_POINTS = 60;

// Daily and weekly series are reduced to one point per calendar month (the last
// observation in that month, NOT a monthly average) so a factsheet chart is not
// carrying 252 rows. Monthly and quarterly series pass through untouched.
function toMonthEnd(
  dates: string[],
  values: Array<number | null>
): { dates: string[]; values: Array<number | null> } {
  const lastOfMonth = new Map<string, { date: string; value: number | null }>();
  dates.forEach((date, idx) => {
    const value = values[idx];
    if (value == null || Number.isNaN(Number(value))) return;
    lastOfMonth.set(String(date).slice(0, 7), { date, value });
  });
  const kept = Array.from(lastOfMonth.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  return { dates: kept.map((k) => k.date), values: kept.map((k) => k.value) };
}

function csvNumber(value: number | null | undefined, digits = 2): string {
  if (value == null || Number.isNaN(Number(value))) return "";
  return Number(value).toFixed(digits);
}

// A catalog `interpretation` describes what the SERIES measures in general. It is
// direction-neutral and says nothing about the current reading, so it must never
// be rendered as a bare trailing sentence under a "supporting" or "watching"
// heading — "wider spreads indicate higher macro stress" appended to a support
// bullet reads as though we are calling stress a support. Label it as background.
function indicatorBullet(
  item: EconSnapshotIndicator,
  formatValue: EconSnapshotInput["formatValue"],
  formatDate: EconSnapshotInput["formatDate"]
): string[] {
  const lines = [`- **${item.label}** — ${formatValue(item)} as of ${formatDate(item.date)}`];
  const background = sanitizeForAdvisor(item.interpretation);
  if (background) lines.push(`  _What this measures:_ ${background}`);
  return lines;
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export function buildEconSnapshotMarkdown(input: EconSnapshotInput): string {
  const {
    asOf,
    generatedAt,
    regimeLabel,
    regimeIsFallback,
    riskBand,
    riskTrend,
    dimensions,
    supports,
    risks,
    indicators,
    regimeHistory,
    weakeningRolling3mPct,
    bands,
    histories,
    formatValue,
    formatDate,
  } = input;

  const regime = REGIME_COPY[regimeLabel] ?? DEFAULT_REGIME_COPY;
  const positioning = POSITIONING_COPY[regimeLabel] ?? DEFAULT_POSITIONING;
  const bandSentence = BAND_COPY[riskBand] ?? BAND_COPY.Benign;
  const trendClause = TREND_COPY[riskTrend] ?? "";
  const asOfIso = (asOf || "").slice(0, 10);
  const generatedIso = generatedAt.slice(0, 10);

  const out: string[] = [];

  // Front matter: lets a designer template auto-populate rather than retype.
  out.push("---");
  out.push('title: "Economic Snapshot"');
  out.push('subtitle: "Helion Strategies macro framework"');
  out.push(`as_of: "${asOfIso}"`);
  out.push(`generated: "${generatedIso}"`);
  out.push(`stance: "${regime.stance}"`);
  out.push(`early_warning: "${riskBand}"`);
  out.push(`early_warning_trend: "${riskTrend}"`);
  out.push("---");
  out.push("");

  out.push("# Economic Snapshot");
  out.push("");
  out.push(`**Data through ${formatDate(asOf)}** · Prepared ${formatDate(generatedAt)}`);
  out.push("");

  if (regimeIsFallback) {
    out.push(
      "> **Preparer note (remove before distribution):** this snapshot was generated while the primary assessment model was unavailable and a simplified fallback was in use. Do not distribute without regenerating."
    );
    out.push("");
  }

  // The read
  out.push("## The read");
  out.push("");
  out.push(`**Current stance: ${regime.stance}**`);
  out.push("");
  out.push(regime.reading);
  out.push("");
  out.push(trendClause ? `${bandSentence.replace(/\.$/, "")} ${trendClause}.` : bandSentence);
  out.push("");

  // Supports
  out.push("## What is supporting the economy");
  out.push("");
  if (supports.length) {
    supports.forEach((item) => out.push(...indicatorBullet(item, formatValue, formatDate)));
  } else {
    out.push("_No broad-based supportive signals in the current reading._");
  }
  out.push("");

  // Watch items
  out.push("## What we are watching");
  out.push("");
  if (risks.length) {
    risks.forEach((item) => out.push(...indicatorBullet(item, formatValue, formatDate)));
  } else {
    out.push("_No immediate macro stress points in the current reading._");
  }
  out.push("");

  // Dimensions
  if (dimensions.length) {
    out.push("## The economy by dimension");
    out.push("");
    out.push(
      "We assess these areas separately so that a single weak or lagging series does not drive the overall picture."
    );
    out.push("");
    out.push("| Area | Current reading |");
    out.push("| --- | --- |");
    dimensions.forEach((dim) => {
      out.push(`| ${escapePipes(dim.label)} | ${escapePipes(dim.status)} |`);
    });
    out.push("");
  }

  // Positioning
  out.push("## What this means for positioning");
  out.push("");
  out.push(positioning);
  out.push("");
  out.push(
    "We do not trade directly off any single economic indicator, and the macro framework does not generate orders. It is one input into a disciplined, rules-based process."
  );
  out.push("");

  // Indicator appendix
  const scored = indicators
    .filter((item) => !item.context_role)
    .filter((item) => item.score >= 0)
    .filter((item) => item.value != null && !Number.isNaN(Number(item.value)));

  if (scored.length) {
    out.push("## Indicator detail");
    out.push("");

    const byTheme = new Map<string, EconSnapshotIndicator[]>();
    scored.forEach((item) => {
      const key = themeFor(item);
      const bucket = byTheme.get(key);
      if (bucket) bucket.push(item);
      else byTheme.set(key, [item]);
    });

    Array.from(byTheme.entries()).forEach(([theme, items]) => {
      out.push(`### ${theme}`);
      out.push("");
      out.push("| Indicator | Latest | As of | Reading |");
      out.push("| --- | --- | --- | --- |");
      items.forEach((item) => {
        out.push(
          `| ${escapePipes(item.label)} | ${escapePipes(formatValue(item))} | ${escapePipes(
            formatDate(item.date)
          )} | ${escapePipes(item.score_label || "—")} |`
        );
      });
      out.push("");
    });
  }

  // Sources
  // ---------------------------------------------------------------------
  // Chart data.
  //
  // The prose above stays qualitative: no section of this document quotes a
  // model probability as a sentence. This appendix publishes the framework's
  // own series so a designer can draw them, which is a different act -- a line
  // showing how a reading has moved over three years is much harder to misread
  // as "there is an X% chance of a recession" than a lone headline figure. The
  // axis labels and the band table below exist to keep that distinction legible.
  // ---------------------------------------------------------------------
  const chartBlocks: string[] = [];

  const rd = regimeHistory?.dates ?? [];
  if (rd.length) {
    chartBlocks.push("### Macro weakening signal");
    chartBlocks.push("");
    chartBlocks.push(
      "Monthly, in percent. `raw` is the framework's month-by-month reading. `avg_3m` is the trailing three-month average, and is the line we treat as the current state — plot it as the primary series and `raw` as a lighter background line if both are shown. This is an internal gauge of how far conditions have moved toward broad economic weakening. It is not a probability of recession and not a forecast."
    );
    chartBlocks.push("");
    chartBlocks.push(HISTORICAL_RESTATEMENT_NOTE);
    chartBlocks.push("");
    chartBlocks.push("```csv");
    chartBlocks.push("date,raw_pct,avg_3m_pct");
    rd.forEach((date, idx) => {
      const raw = regimeHistory.weakening_prob?.[idx];
      chartBlocks.push(
        `${date},${csvNumber(raw == null ? null : raw * 100)},${csvNumber(weakeningRolling3mPct?.[idx])}`
      );
    });
    chartBlocks.push("```");
    chartBlocks.push("");

    if (bands) {
      chartBlocks.push(
        "Reference bands for the chart above. Each is the lower bound of that band, in percent — draw them as horizontal thresholds or shaded regions."
      );
      chartBlocks.push("");
      chartBlocks.push("| Band | From (%) |");
      chartBlocks.push("| --- | --- |");
      ([
        ["Sentinel", bands.sentinel],
        ["Monitor", bands.monitor],
        ["Watch", bands.watch],
        ["Caution", bands.caution],
        ["Severe", bands.severe],
      ] as Array<[string, number]>).forEach(([name, value]) => {
        chartBlocks.push(`| ${name} | ${csvNumber(value * 100, 1)} |`);
      });
      chartBlocks.push("");
    }

    chartBlocks.push("### Framework reading over time");
    chartBlocks.push("");
    chartBlocks.push(
      "Monthly, in percent, summing to 100. How the framework has characterised the backdrop over the trailing window. Suits a stacked area chart. The same restatement note as above applies."
    );
    chartBlocks.push("");
    chartBlocks.push("```csv");
    chartBlocks.push("date,expansionary_pct,cautionary_pct,recessionary_pct");
    rd.forEach((date, idx) => {
      chartBlocks.push(
        [
          date,
          csvNumber((regimeHistory.expansionary?.[idx] ?? 0) * 100),
          csvNumber((regimeHistory.cautionary?.[idx] ?? 0) * 100),
          csvNumber((regimeHistory.recessionary?.[idx] ?? 0) * 100),
        ].join(",")
      );
    });
    chartBlocks.push("```");
    chartBlocks.push("");
  }

  CHART_SERIES.forEach((spec) => {
    const history = histories?.[spec.id];
    if (!history || !history.dates?.length) return;

    const cadence = (history.cadence || "").toLowerCase();
    const reduced =
      cadence === "daily" || cadence === "weekly"
        ? toMonthEnd(history.dates, history.values)
        : { dates: history.dates, values: history.values };

    const start = Math.max(0, reduced.dates.length - CHART_MAX_POINTS);
    const dates = reduced.dates.slice(start);
    const values = reduced.values.slice(start);
    if (!dates.length) return;

    chartBlocks.push(`### ${history.label || spec.id}`);
    chartBlocks.push("");
    const reducedNote =
      cadence === "daily" || cadence === "weekly"
        ? " Reduced to one point per month (the last observation in each month, not a monthly average) to keep the chart readable."
        : "";
    chartBlocks.push(`${spec.note}${reducedNote}`);
    chartBlocks.push("");
    chartBlocks.push("```csv");
    chartBlocks.push("date,value");
    dates.forEach((date, idx) => chartBlocks.push(`${date},${csvNumber(values[idx], 4)}`));
    chartBlocks.push("```");
    chartBlocks.push("");
  });

  if (chartBlocks.length) {
    out.push("## Chart data");
    out.push("");
    out.push(
      "Series for the designer, as CSV blocks. Each can be pasted straight into a spreadsheet or charting tool. Dates are the observation date, not the publication date."
    );
    out.push("");
    out.push(...chartBlocks);
  }

  out.push("## Sources and methodology");
  out.push("");
  out.push(SOURCE_NOTE);
  out.push("");

  // Disclosures
  out.push("## Important disclosures");
  out.push("");
  DISCLOSURES.forEach((line) => {
    out.push(line);
    out.push("");
  });

  out.push("---");
  out.push("");
  out.push("Helion Strategies, LLC · Helion Strategies Meridian Fund, LLC");
  out.push("");

  return out.join("\n");
}

export function econSnapshotFilename(asOf: string | null, generatedAt: string): string {
  const stamp = (asOf || generatedAt || "").slice(0, 10) || "undated";
  return `helion-economic-snapshot-${stamp}.md`;
}

export function downloadEconSnapshot(input: EconSnapshotInput): void {
  const markdown = buildEconSnapshotMarkdown(input);
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = econSnapshotFilename(input.asOf, input.generatedAt);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
